import React, { PureComponent } from 'react';
import {
  ACCOUNT_SELECTOR_CLICK,
  USER_SETTINGS_CLICK,
  logEvent,
} from '../../utils/analytics';
import {
  faCode,
  faSignOutAlt,
  faUser,
  faUserCog,
} from '@fortawesome/pro-solid-svg-icons';
import { Avatar } from 'Common/components/Atoms/Avatar';
import Selector, { Item, Organization } from '../../components/Selector';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { Theme } from '@mui/material/styles';
import withTheme from '@mui/styles/withTheme';
import withStyles from '@mui/styles/withStyles';
import { User } from '../../models/User';
import { accountSelector } from '../../selectors/Account';
import { actions as LoginActions } from 'reducers/login';

const styles = (theme: Theme) => ({
  avatar: {
    marginRight: theme.spacing(1),
    marginLeft: theme.spacing(2),
  },
});

interface Props {
  classes: {
    [key: string]: string;
  };
  user: User | null;
  theme: Theme;
  organization: Organization;
  logout: () => void;
}

export class AccountSelector extends PureComponent<Props> {
  getItems = () => {
    const { user, logout } = this.props;
    const items: Item[] = [];
    if (user?.is_reviewer) {
      items.push({
        title: 'Reviewer Dash',
        url: '/reviewer',
        icon: faCode,
      });
    }

    /**
     * DropMenu component requires url prop for links, so dummy urls are provided for code challenges
     * and logout, while the actual redirection is handled by the onClick.
     */
    if (user?.uses_code_challenges) {
      items.push({
        title: 'Code Challenges',
        url: '/code-challenges',
        icon: faCode,
        onClick: (e) => {
          e?.preventDefault();
          window.location.href =
            process.env.REACT_APP_CODE_CHALLENGES_URL || '';
        },
      });
    }

    if (user?.terms_accepted) {
      items.push({
        title: 'User Settings',
        url: '/dash/user/settings',
        icon: faUser,
        onClick: () => {
          logEvent(USER_SETTINGS_CLICK);
        },
      });
    }

    if (user?.roles?.includes('admin')) {
      items.push({
        title: 'Admin',
        url: '/admin/dash',
        icon: faUserCog,
        onClick: (e) => {
          e?.preventDefault();

          // Use window.location.href rather than react-router
          // because admin is in a separate context
          window.location.href = '/admin/dash';
        },
      });
    }

    items.push({
      title: 'Log out',
      url: '/',
      icon: faSignOutAlt,
      onClick: (e) => {
        e?.preventDefault();
        logout();
      },
    });

    return items;
  };

  render(): JSX.Element {
    const { classes, user, theme } = this.props;
    const items = this.getItems();

    return (
      <Selector
        items={items}
        title={
          <div className={classes.avatar}>
            <Avatar
              src={user?.photo_url}
              alt={user?.name.toUpperCase()}
              color={theme.palette.info.dim}
              size="30px"
              fontSize="12px"
              cursor="pointer"
            />
          </div>
        }
        id="accountSelector"
        width={200}
        onClick={() => logEvent(ACCOUNT_SELECTOR_CLICK)}
      />
    );
  }
}

function mapStateToProps(state: any, ownProps: Props) {
  const { organization } = ownProps;
  const account = accountSelector(state);
  return {
    user: account.user,
    organization,
  };
}

function mapDispatchToProps(dispatch) {
  return bindActionCreators({ ...LoginActions }, dispatch);
}

export default withTheme(
  withStyles(styles)(
    connect(mapStateToProps, mapDispatchToProps)(AccountSelector)
  )
);
